import {TFunction} from "i18next"
import {compact} from "lodash"
import {FC, useMemo} from "react"
import {useNavigate, useSearchParams} from "react-router-dom"
import {ReactSVG} from "react-svg"

import {Button, Tag} from "@frontend/components"
import chevron from "@frontend/design/icons/chevron-down.svg"
import currencyCircleDollarIcon from "@frontend/design/icons/currency-circle-dollar.svg"
import deliveryIcon from "@frontend/design/icons/delivery.svg"
import mapIcon from "@frontend/design/icons/map.svg"
import recycleIcon from "@frontend/design/icons/recycle.svg"
import skillsIcon from "@frontend/design/icons/skills.svg"
import timeIcon from "@frontend/design/icons/time.svg"
import {useTranslation} from "@frontend/i18n"
import {makePath} from "@frontend/routing"
import {activeTheme} from "@frontend/themes/activeTheme"
import {resolveConsiderBullets} from "@frontend/themes/considerLists"
import type {ConsiderIcon, ConsiderListsByProfile} from "@frontend/themes/types"
import {
  buildCaseInfo,
  buildRecommendationContext,
  hostNavigate,
  resolveRecommendationUrl,
  toCaseInfoInput,
} from "@frontend/utils/appHostFunctions"
import {
  emitHostNextStepSelected,
  remediationTypeToHostJourneyStep,
} from "@frontend/utils/hostJourneyEvents"
import {trackEvent} from "@frontend/utils/trackEvent"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import type {
  RemediationData,
  RemediationPageData,
} from "@ri2/app/server/routers/getRemediationPageData"
import type {EnrichedProductInfo} from "@ri2/db/client"
import {SkillLevel} from "@ri2/db/client"
import {css, cx} from "@styled-system/css"
import {hstack, iconsColor, vstack} from "@styled-system/patterns"

import {Price} from "../price"

/**
 * `remediationCtaOrder` is a theme capability (see `themes/types.ts`):
 * appliance-service brands reserve the secondary fill for the page's main CTA
 * and primary for the other options; catalog brands use primary for the
 * recommended option.
 */
const recommendedActionVariant =
  activeTheme.capabilities.remediationCtaOrder === "recommendedSecondary"
    ? "secondary"
    : "primary"
const alternateActionVariant =
  activeTheme.capabilities.remediationCtaOrder === "recommendedSecondary"
    ? "primary"
    : "secondary"

const remediationCopy = activeTheme.remediationCopy
const supportingTextIsEmphasis =
  activeTheme.capabilities.supportingText === "emphasis"
const supportingCaptionColor = supportingTextIsEmphasis
  ? "text.primary"
  : "text.quaternary"
const supportingHeadingColor = supportingTextIsEmphasis
  ? "text.primary"
  : "text.tertiary"

const {accentGlyph: homeVector} = activeTheme.assets

const considerIconSrc: Record<ConsiderIcon, string> = {
  time: timeIcon,
  skills: skillsIcon,
  map: mapIcon,
  delivery: deliveryIcon,
  recycle: recycleIcon,
  currency: currencyCircleDollarIcon,
}

const themedConsiderItems = (
  lists: ConsiderListsByProfile,
  type: RemediationData["type"],
  considerProfile: string | null,
  skillLevelRequired: SkillLevel | null,
  t: TFunction,
): ConsiderItemProps[] =>
  resolveConsiderBullets(
    lists,
    type,
    considerProfile,
    skillLevelRequired
      ? (t(`cause.nextStep.skillLevels.${skillLevelRequired}`) as string)
      : null,
  ).map(({icon, label}) => ({icon: considerIconSrc[icon], label}))

const doItForMeConsiderItems = (
  skillLevelRequired: SkillLevel | null,
  t: TFunction,
): ConsiderItemProps[] => {
  return compact([
    {
      icon: mapIcon,
      label: t("cause.nextStep.doItForMe.localAvailability"),
    },
    skillLevelRequired && {
      icon: skillsIcon,
      label: t("cause.nextStep.doItForMe.skillLevel", {
        skillLevel: t(`cause.nextStep.skillLevels.${skillLevelRequired}`),
      }),
    },
    {
      icon: timeIcon,
      label: t("cause.nextStep.doItForMe.numberOfTechnicianVisits"),
    },
  ])
}

const diyConsiderItems = (
  skillLevelRequired: SkillLevel | null,
  t: TFunction,
): (ConsiderItemProps | undefined)[] => {
  if (skillLevelRequired) {
    return [
      {
        icon: deliveryIcon,
        label: t("cause.nextStep.diy.shipping"),
      },
      {
        icon: timeIcon,
        label: t("cause.nextStep.diy.age"),
      },
      {
        icon: skillsIcon,
        label: t("cause.nextStep.diy.difficulty", {
          skillLevel: t(`cause.nextStep.skillLevels.${skillLevelRequired}`),
        }),
      },
    ]
  }

  return [
    {
      icon: timeIcon,
      label: t("cause.nextStep.diy.repairTime"),
    },
    {
      icon: skillsIcon,
      label: t("cause.nextStep.diy.difficultyGeneric"),
    },
  ]
}

const replaceConsiderItems = (
  t: TFunction,
): (ConsiderItemProps | undefined)[] => {
  return [
    {
      icon: timeIcon,
      label: t("cause.nextStep.replace.installationTimeframe"),
    },
    {
      icon: currencyCircleDollarIcon,
      label: t("cause.nextStep.replace.installationFees"),
    },
    {
      icon: recycleIcon,
      label: t("cause.nextStep.replace.disposal"),
    },
  ]
}

const actionLabel = (type: RemediationData["type"], t: TFunction): string => {
  if (remediationCopy && type === "replaceRemediation") {
    return remediationCopy.replaceCta
  }
  return t(`cause.nextStep.action.${type}`) as string
}

interface ConsiderItemProps {
  label: string
  icon: string
}

const ConsiderItem: FC<ConsiderItemProps> = ({label, icon}) => (
  <div
    className={css({
      minHeight: 20,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      ...(supportingTextIsEmphasis ? {color: "text.primary"} : {}),
    })}
  >
    <div
      className={css({
        height: 20,
        width: 20,
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        "& svg": {width: "100%", height: "100%"},
        ...(supportingTextIsEmphasis ? {color: "text.primary"} : {}),
      })}
    >
      <ReactSVG src={icon} />
    </div>
    <div
      className={css({
        textStyle: "captionRegular",
        color: supportingCaptionColor,
      })}
    >
      {label}
    </div>
  </div>
)

interface Props {
  caseId: string
  causeOnMessageId: number | "peek"
  data: RemediationData
  productInfo: EnrichedProductInfo
  causes: CaseCauseInfo[]
  parts: CasePartInfo[]
  applianceAgeMonths?: number | null
  agentProfileId: RemediationPageData["agentProfileId"]
  className?: string
  wrapperClassName?: string
  isReplacePriceLoading?: boolean
}

export const Remediation: FC<Props> = ({
  caseId,
  causeOnMessageId,
  data: {
    isRecommended,
    type,
    cost,
    skillLevelRequired,
    productTypeMetadata,
    onlyShowPriceRanges,
    enableActionButton: showActionButton,
    hostEstimate,
    considerProfile,
  },
  productInfo,
  causes,
  parts,
  applianceAgeMonths,
  agentProfileId,
  className,
  wrapperClassName,
  isReplacePriceLoading,
}) => {
  const product = useMemo(
    () => toCaseInfoInput({productInfo, causes, parts, applianceAgeMonths}),
    [productInfo, causes, parts, applianceAgeMonths],
  )
  const displayCost = cost
  const replaceUrl =
    type === "replaceRemediation"
      ? (hostEstimate?.replaceUrl ?? productTypeMetadata?.replaceUrl ?? null)
      : (productTypeMetadata?.replaceUrl ?? null)

  const fallbackNavigateTo =
    type === "diyRemediation"
      ? makePath({
          name: "cases",
          caseId,
          causeOnMessageId,
          diy: true,
        })
      : type === "difmRemediation"
        ? makePath({
            name: "cases",
            caseId,
            causeOnMessageId,
            difm: true,
          })
        : replaceUrl

  const navigate = useNavigate()
  const t = useTranslation()

  const {
    setFalse: collapseConsiderationItems,
    setTrue: expandConsiderationItems,
    state: isExpandedConsiderationItems,
  } = useBooleanState(false)

  const considerItems = remediationCopy
    ? themedConsiderItems(
        remediationCopy.considerLists,
        type,
        considerProfile,
        skillLevelRequired,
        t,
      )
    : type === "difmRemediation"
      ? doItForMeConsiderItems(skillLevelRequired, t)
      : type === "replaceRemediation"
        ? replaceConsiderItems(t)
        : diyConsiderItems(skillLevelRequired, t)
  const filteredConsiderItems = considerItems.filter(
    (labelAndIcon): labelAndIcon is ConsiderItemProps => !!labelAndIcon,
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams()

  const onReplace = (): void => {
    setSearchParams({replace: "true"})
  }

  const onClick = (): void => {
    if (type === "replaceRemediation") {
      trackEvent("chat_see_options")
    } else if (type === "difmRemediation") {
      trackEvent("chat_find_pros")
    } else {
      trackEvent("chat_show_me_how")
    }

    const caseInfo = buildCaseInfo({caseId, product})
    emitHostNextStepSelected(
      caseId,
      remediationTypeToHostJourneyStep(type),
      caseInfo,
    )

    const hostUrl = resolveRecommendationUrl(
      buildRecommendationContext({
        remediationType: type,
        caseId,
        product,
      }),
      {agentProfileId},
    )
    if (hostUrl) {
      hostNavigate(hostUrl)
      return
    }

    if (type === "replaceRemediation") {
      onReplace()
      if (fallbackNavigateTo) hostNavigate(fallbackNavigateTo)
      return
    }

    if (fallbackNavigateTo) navigate(fallbackNavigateTo)
  }

  return (
    <div
      className={cx(
        css({
          overflow: "visible",
          position: "relative",
        }),
        wrapperClassName,
      )}
    >
      {isRecommended && (
        <div
          className={css({
            position: "absolute",
            top: "-19px",
            right: "-20px",
            largeDesktop: {
              top: "-23.29px",
              right: "-24.93px",
            },
            desktop: {
              top: "-13.76px",
              right: "-14.76px",
            },
            zIndex: "causes.sparkle",
          })}
        >
          <ReactSVG
            src={homeVector}
            className={cx(
              iconsColor({color: "foreground.brand.accent"}),
              css({
                "& svg": {
                  width: "40px",
                  height: "40px",
                  largeDesktop: {
                    width: "49.86px",
                    height: "49.86px",
                  },
                  desktop: {
                    width: "30.72px",
                    height: "30.72px",
                  },
                },
              }),
            )}
          />
        </div>
      )}
      <div
        className={cx(
          css(
            {
              borderRadius: 12,
              display: "flex",
              flexDirection: "column",
              backgroundColor: isRecommended
                ? "background.primary"
                : "background.muted",
              borderWidth: 1,
              // Recommended: solid edge; others: soft mix so they recede.
              borderColor: isRecommended
                ? "border.primary.300"
                : "color-mix(in srgb, token(colors.border.primary.300) 35%, transparent)",
              padding: 20,
              largeDesktopOnly: {
                "@media (max-height: 800px)": {
                  paddingBottom: 0,
                  overflow: "auto",
                },
              },
            },
            isRecommended && {
              boxShadow:
                "0px 9px 20px rgba(28, 37, 72, 0.14), 0px 3px 3px rgba(28, 37, 72, 0.06)",
            },
          ),
          className,
        )}
      >
        <div
          className={vstack({
            flex: 1,
            alignItems: "stretch",
            gap: {
              base: 28,
              largeDesktop: 36,
            },
            "@media (max-height: 800px)": {
              largeDesktop: {
                gap: 28,
              },
            },
          })}
        >
          <div
            className={hstack({
              justifyContent: "space-between",
              minHeight: 24,
              flexWrap: "wrap",
            })}
          >
            <span
              className={css({
                textStyle: "bodyLargeMedium",
                fontWeight:
                  activeTheme.capabilities.remediationEmphasis === "bold"
                    ? 700
                    : undefined,
                color: "text.primary",
                flexShrink: 0,
              })}
            >
              {t(`cause.nextStep.titles.${type}`) as string}
            </span>
            {isRecommended && (
              <Tag
                title={t("cause.nextStep.recommended")}
                className={css({flexShrink: 0})}
                colorScheme="recommended"
              />
            )}
          </div>
          <div
            className={cx(
              vstack({
                gap: 28,
                alignItems: "flex-start",
                "@media (max-height: 800px)": {
                  gap: 8,
                },
              }),
              css(
                // remove marginBottom when is not expanded to avoid extra space and unnecessary scroll
                isExpandedConsiderationItems
                  ? {
                      "@media (max-height: 800px)": {
                        largeDesktop: {
                          marginBottom: 0,
                        },
                      },
                    }
                  : {
                      "@media (max-height: 800px)": {
                        largeDesktop: {
                          marginBottom: -28,
                        },
                      },
                    },
              ),
            )}
          >
            <Price
              cost={displayCost}
              type={type}
              onlyShowRange={onlyShowPriceRanges && !hostEstimate}
              isLoading={type === "replaceRemediation" && isReplacePriceLoading}
              disclosure={
                type === "replaceRemediation"
                  ? (hostEstimate?.disclosure ?? null)
                  : null
              }
            />
            <button
              onClick={
                isExpandedConsiderationItems
                  ? collapseConsiderationItems
                  : expandConsiderationItems
              }
              className={hstack({
                display: "none",
                largeDesktopOnly: {
                  "@media (max-height: 800px)": {display: "flex", paddingY: 8},
                },
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 8,
              })}
            >
              <ReactSVG
                src={chevron}
                className={css({
                  transform: isExpandedConsiderationItems
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                })}
              />
              <h3
                className={css({
                  textStyle: "bodyRegular",
                  color: supportingHeadingColor,
                })}
              >
                {t("cause.nextStep.moreToConsider")}
              </h3>
            </button>
            <div
              className={cx(
                css({
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  desktopDown: {
                    display: "none",
                  },
                }),
                isExpandedConsiderationItems
                  ? css({
                      "@media (max-height: 800px)": {
                        display: "flex",
                      },
                    })
                  : css({
                      "@media (max-height: 800px)": {
                        display: "none",
                        position: "absolute",
                      },
                    }),
              )}
            >
              <h3
                className={css({
                  textStyle: "bodyRegular",
                  color: supportingHeadingColor,
                  "@media (max-height: 800px)": {display: "none"},
                })}
              >
                {t("cause.nextStep.moreToConsider")}
              </h3>
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                })}
              >
                {filteredConsiderItems.map(({label, icon}) => (
                  <ConsiderItem label={label} icon={icon} key={label} />
                ))}
              </div>
            </div>
          </div>
          <div
            className={css({
              marginTop: "auto",
              backgroundColor: isRecommended
                ? "background.primary"
                : "background.muted",
              largeDesktopOnly: {
                "@media (max-height: 800px)": {
                  position: "sticky",
                  paddingBottom: 20,
                  bottom: 0,
                },
              },
            })}
          >
            <Button
              id={idForActionButton(type)}
              onClick={onClick}
              disabled={!showActionButton}
              title={actionLabel(type, t)}
              variant={
                isRecommended
                  ? recommendedActionVariant
                  : alternateActionVariant
              }
              fullWidth
              rightElement={
                isRecommended && (
                  <ReactSVG
                    src={chevron}
                    className={css({
                      transform: "rotate(-90deg)",
                    })}
                  />
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const idForActionButton = (type: string): string => `remediation-action-${type}`
